/**
 * Apps2Go Hub – Henkilöstökortti (StaffProfile)
 *
 * Avautuu /admin/staff/:id reitiltä. Sisältää 7 välilehteä:
 *  1. Yleiskatsaus    – nopea katsaus + tila
 *  2. Yhteystiedot   – osoite, puhelin, hätäyhteys
 *  3. Pankki & vero  – IBAN/BIC + verokorttitiedot + PDF-skannaus
 *  4. Työsuhde       – rooli, palkka, sopimus
 *  5. Pätevyydet     – strukturoidut: hygieniapassi, anniskelu, EA jne.
 *  6. Liitteet       – PDF/kuva-tiedostot (verokortti-skannaus, todistukset)
 *  7. Oikeudet       – per-moduuli access_level + actions + scope + role
 *
 * Ylätunnisteessa nopeat linkit työvuoroihin ja palkkalaskelmiin.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, User, Phone, Banknote, Briefcase, GraduationCap, Paperclip,
  Shield, Save, Loader2, Trash2, Upload, FileText, Calendar, Receipt, Plus,
  AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

/* ── helpers ─────────────────────────────────────────────────────────── */
const MODULES = [
  { key: "invoicing",   label: "Laskutus" },
  { key: "accounting",  label: "Kirjanpito" },
  { key: "ereceipts",   label: "E-kuitti" },
  { key: "hr",          label: "HR & Palkat" },
  { key: "shifts",      label: "Työvuorot" },
  { key: "restaurant",  label: "Ravintola" },
  { key: "meeting",     label: "Kokoushuone" },
  { key: "customers",   label: "Asiakkaat" },
  { key: "reports",     label: "Raportit" },
];

const ACCESS_LEVELS = [
  { value: "none",    label: "Ei oikeutta",       color: "text-white/40",   bg: "bg-white/5" },
  { value: "view",    label: "Vain katselu",      color: "text-cyan-300",   bg: "bg-cyan-500/10" },
  { value: "partial", label: "Osittainen",        color: "text-amber-300",  bg: "bg-amber-500/10" },
  { value: "full",    label: "Täydet oikeudet",   color: "text-emerald-300",bg: "bg-emerald-500/10" },
];

const ACTIONS = [
  { value: "create", label: "Luo" },
  { value: "edit",   label: "Muokkaa" },
  { value: "delete", label: "Poista" },
  { value: "send",   label: "Lähetä" },
  { value: "export", label: "Vie" },
];

const SCOPES = [
  { value: "own",  label: "Vain omat" },
  { value: "team", label: "Tiimin" },
  { value: "all",  label: "Kaikki" },
];

const CERT_TYPES = [
  "Hygieniapassi", "Anniskelupassi", "Ensiapu (EA1)", "Tieturva 1",
  "Työturvallisuuskortti", "Trukkikortti", "Tulityökortti", "Ajokortti",
  "Muu",
];

/* ── component ───────────────────────────────────────────────────────── */
export default function StaffProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/staff/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/admin/users");
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = async (partial) => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/staff/${id}`, partial);
      setData(r.data);
      toast.success("Tallennettu");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/40">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6 max-w-6xl mx-auto" data-testid="staff-profile">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Button variant="ghost" onClick={() => navigate("/admin/users")} className="text-white/60" data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80">Henkilöstökortti</p>
            <h1 className="text-3xl font-display tracking-tight">{data.full_name}</h1>
            <div className="flex gap-2 items-center flex-wrap mt-1">
              <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">{data.role}</Badge>
              <span className="text-xs text-white/40">{data.email}</span>
              {data.position && <span className="text-xs text-white/60">· {data.position}</span>}
              {!data.is_active && <Badge className="bg-red-500/15 text-red-300">Ei aktiivinen</Badge>}
              {data.twofa_enabled && <Badge className="bg-emerald-500/15 text-emerald-300">2FA</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/shifts?employee=${id}`)} className="border-cyan-400/30 text-cyan-200" data-testid="link-shifts">
            <Calendar className="w-4 h-4 mr-1" /> Työvuorot
          </Button>
          <Button variant="outline" onClick={() => navigate(`/hr?employee=${id}`)} className="border-emerald-400/30 text-emerald-200" data-testid="link-payroll">
            <Receipt className="w-4 h-4 mr-1" /> Palkkalaskelmat
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex flex-wrap gap-1" data-testid="profile-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview"><User className="w-3.5 h-3.5 mr-1" /> Yleiskatsaus</TabsTrigger>
          <TabsTrigger value="contact"  data-testid="tab-contact"><Phone className="w-3.5 h-3.5 mr-1" /> Yhteystiedot</TabsTrigger>
          <TabsTrigger value="bank"     data-testid="tab-bank"><Banknote className="w-3.5 h-3.5 mr-1" /> Pankki & vero</TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment"><Briefcase className="w-3.5 h-3.5 mr-1" /> Työsuhde</TabsTrigger>
          <TabsTrigger value="certs"    data-testid="tab-certs"><GraduationCap className="w-3.5 h-3.5 mr-1" /> Pätevyydet</TabsTrigger>
          <TabsTrigger value="docs"     data-testid="tab-docs"><Paperclip className="w-3.5 h-3.5 mr-1" /> Liitteet</TabsTrigger>
          <TabsTrigger value="perms"    data-testid="tab-perms"><Shield className="w-3.5 h-3.5 mr-1" /> Oikeudet</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab data={data} /></TabsContent>
        <TabsContent value="contact"><ContactTab data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="bank"><BankTab data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="employment"><EmploymentTab data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="certs"><CertsTab data={data} save={save} saving={saving} /></TabsContent>
        <TabsContent value="docs"><DocsTab data={data} staffId={id} reload={load} /></TabsContent>
        <TabsContent value="perms"><PermsTab data={data} save={save} saving={saving} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ── Overview ────────────────────────────────────────────────────────── */
function OverviewTab({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <KpiCard label="Pätevyyksiä" value={data.certifications_count} icon={GraduationCap} color="text-emerald-300" />
      <KpiCard label="Liitteitä" value={data.documents_count} icon={Paperclip} color="text-cyan-300" />
      <KpiCard label="Moduulioikeuksia" value={data.permissions_count} icon={Shield} color="text-amber-300" />

      <div className="md:col-span-3 glass-card p-5 space-y-2">
        <p className="text-xs uppercase tracking-widest text-white/40">Lyhennelmä</p>
        <Field label="Sähköposti" value={data.email} />
        <Field label="Puhelin" value={data.phone} />
        <Field label="Tehtävänimike" value={data.position} />
        <Field label="Osasto" value={data.department} />
        <Field label="Aloituspäivä" value={data.employment?.start_date} />
        {data.notes && (
          <div className="pt-2 border-t border-white/5">
            <p className="text-xs text-white/40">Muistiinpanot</p>
            <p className="text-sm text-white/80">{data.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="glass-card p-4">
      <Icon className={`w-5 h-5 mb-2 ${color}`} />
      <p className="text-2xl font-display">{value || 0}</p>
      <p className="text-xs text-white/40">{label}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between border-b border-white/5 py-1 text-sm">
      <span className="text-white/50">{label}</span>
      <span className="text-white/90">{value || <span className="text-white/30 italic">tyhjä</span>}</span>
    </div>
  );
}

/* ── Contact ─────────────────────────────────────────────────────────── */
function ContactTab({ data, save, saving }) {
  const [form, setForm] = useState({
    phone: data.phone || "",
    personal: {
      date_of_birth: data.personal?.date_of_birth || "",
      ssn: data.personal?.ssn || "",
      address: data.personal?.address || "",
      city: data.personal?.city || "",
      postal_code: data.personal?.postal_code || "",
      country: data.personal?.country || "FI",
      nationality: data.personal?.nationality || "",
      emergency_contact: {
        name: data.personal?.emergency_contact?.name || "",
        phone: data.personal?.emergency_contact?.phone || "",
        relation: data.personal?.emergency_contact?.relation || "",
      },
    },
  });
  return (
    <div className="glass-card p-5 space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Puhelin" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="ct-phone" />
        <FormField label="Syntymäaika" type="date" value={form.personal.date_of_birth} onChange={(v) => setForm({ ...form, personal: { ...form.personal, date_of_birth: v } })} testid="ct-dob" />
        <FormField label="Henkilötunnus" value={form.personal.ssn} onChange={(v) => setForm({ ...form, personal: { ...form.personal, ssn: v } })} testid="ct-ssn" />
        <FormField label="Kansalaisuus" value={form.personal.nationality} onChange={(v) => setForm({ ...form, personal: { ...form.personal, nationality: v } })} testid="ct-nat" />
        <FormField label="Katuosoite" value={form.personal.address} onChange={(v) => setForm({ ...form, personal: { ...form.personal, address: v } })} className="col-span-2" testid="ct-addr" />
        <FormField label="Postinumero" value={form.personal.postal_code} onChange={(v) => setForm({ ...form, personal: { ...form.personal, postal_code: v } })} testid="ct-zip" />
        <FormField label="Kaupunki" value={form.personal.city} onChange={(v) => setForm({ ...form, personal: { ...form.personal, city: v } })} testid="ct-city" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-amber-300/80 mb-2 mt-3">Hätäyhteys</p>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Nimi" value={form.personal.emergency_contact.name} onChange={(v) => setForm({ ...form, personal: { ...form.personal, emergency_contact: { ...form.personal.emergency_contact, name: v } } })} testid="ct-emerg-name" />
          <FormField label="Puhelin" value={form.personal.emergency_contact.phone} onChange={(v) => setForm({ ...form, personal: { ...form.personal, emergency_contact: { ...form.personal.emergency_contact, phone: v } } })} testid="ct-emerg-phone" />
          <FormField label="Suhde" value={form.personal.emergency_contact.relation} onChange={(v) => setForm({ ...form, personal: { ...form.personal, emergency_contact: { ...form.personal.emergency_contact, relation: v } } })} testid="ct-emerg-rel" />
        </div>
      </div>
      <Button onClick={() => save(form)} disabled={saving} className="btn-luxe" data-testid="contact-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3.5 h-3.5 mr-2" />}
        Tallenna yhteystiedot
      </Button>
    </div>
  );
}

/* ── Bank ────────────────────────────────────────────────────────────── */
function BankTab({ data, save, saving }) {
  const [form, setForm] = useState({
    bank: {
      iban: data.bank?.iban || "",
      bic: data.bank?.bic || "",
      bank_name: data.bank?.bank_name || "",
      account_holder: data.bank?.account_holder || data.full_name || "",
    },
    tax_card: {
      base_percent: data.tax_card?.base_percent ?? "",
      additional_percent: data.tax_card?.additional_percent ?? "",
      income_threshold: data.tax_card?.income_threshold ?? "",
      valid_from: data.tax_card?.valid_from || "",
      valid_to: data.tax_card?.valid_to || "",
      card_number: data.tax_card?.card_number || "",
      notes: data.tax_card?.notes || "",
    },
  });
  return (
    <div className="space-y-4" data-testid="bank-form">
      <div className="glass-card p-5 space-y-3">
        <p className="text-xs uppercase tracking-widest text-amber-300/80">Pankkitiedot</p>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="IBAN" value={form.bank.iban} onChange={(v) => setForm({ ...form, bank: { ...form.bank, iban: v } })} placeholder="FI00 0000 0000 0000 00" testid="bk-iban" />
          <FormField label="BIC / SWIFT" value={form.bank.bic} onChange={(v) => setForm({ ...form, bank: { ...form.bank, bic: v } })} placeholder="NDEAFIHH" testid="bk-bic" />
          <FormField label="Pankki" value={form.bank.bank_name} onChange={(v) => setForm({ ...form, bank: { ...form.bank, bank_name: v } })} placeholder="Nordea / OP / S-Pankki" testid="bk-bank" />
          <FormField label="Tilinhaltija" value={form.bank.account_holder} onChange={(v) => setForm({ ...form, bank: { ...form.bank, account_holder: v } })} testid="bk-holder" />
        </div>
      </div>

      <div className="glass-card p-5 space-y-3">
        <p className="text-xs uppercase tracking-widest text-amber-300/80">Verokortti</p>
        <p className="text-[11px] text-white/40 -mt-1">
          Syötä manuaalisesti tai lataa skannattu PDF &quot;Liitteet&quot;-välilehdellä.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Perusprosentti %" type="number" value={form.tax_card.base_percent} onChange={(v) => setForm({ ...form, tax_card: { ...form.tax_card, base_percent: v ? parseFloat(v) : null } })} testid="tx-base" />
          <FormField label="Lisäprosentti %" type="number" value={form.tax_card.additional_percent} onChange={(v) => setForm({ ...form, tax_card: { ...form.tax_card, additional_percent: v ? parseFloat(v) : null } })} testid="tx-add" />
          <FormField label="Tuloraja €" type="number" value={form.tax_card.income_threshold} onChange={(v) => setForm({ ...form, tax_card: { ...form.tax_card, income_threshold: v ? parseFloat(v) : null } })} testid="tx-thr" />
          <FormField label="Voimassa alkaen" type="date" value={form.tax_card.valid_from} onChange={(v) => setForm({ ...form, tax_card: { ...form.tax_card, valid_from: v } })} testid="tx-from" />
          <FormField label="Voimassa asti" type="date" value={form.tax_card.valid_to} onChange={(v) => setForm({ ...form, tax_card: { ...form.tax_card, valid_to: v } })} testid="tx-to" />
          <FormField label="Verokortin numero" value={form.tax_card.card_number} onChange={(v) => setForm({ ...form, tax_card: { ...form.tax_card, card_number: v } })} testid="tx-num" />
        </div>
      </div>

      <Button onClick={() => save(form)} disabled={saving} className="btn-luxe" data-testid="bank-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3.5 h-3.5 mr-2" />}
        Tallenna pankki- ja verotiedot
      </Button>
    </div>
  );
}

/* ── Employment ──────────────────────────────────────────────────────── */
function EmploymentTab({ data, save, saving }) {
  const [form, setForm] = useState({
    employment: {
      employment_type: data.employment?.employment_type || "",
      position: data.employment?.position || "",
      department: data.employment?.department || "",
      start_date: data.employment?.start_date || "",
      end_date: data.employment?.end_date || "",
      hourly_rate: data.employment?.hourly_rate ?? "",
      monthly_salary: data.employment?.monthly_salary ?? "",
      weekly_hours: data.employment?.weekly_hours ?? "",
      contract_type: data.employment?.contract_type || "",
      probation_end: data.employment?.probation_end || "",
      holiday_balance_days: data.employment?.holiday_balance_days ?? "",
    },
  });
  return (
    <div className="glass-card p-5 space-y-3" data-testid="employment-form">
      <p className="text-xs uppercase tracking-widest text-amber-300/80">Työsuhde</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Työsuhteen tyyppi</Label>
          <select
            value={form.employment.employment_type}
            onChange={(e) => setForm({ ...form, employment: { ...form.employment, employment_type: e.target.value } })}
            className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm mt-1"
            data-testid="emp-type"
          >
            <option value="">Valitse…</option>
            <option value="hourly">Tuntipalkkainen</option>
            <option value="monthly">Kuukausipalkkainen</option>
            <option value="freelance">Freelance</option>
            <option value="trainee">Harjoittelija</option>
            <option value="other">Muu</option>
          </select>
        </div>
        <FormField label="Tehtävänimike" value={form.employment.position} onChange={(v) => setForm({ ...form, employment: { ...form.employment, position: v } })} testid="emp-pos" />
        <FormField label="Osasto" value={form.employment.department} onChange={(v) => setForm({ ...form, employment: { ...form.employment, department: v } })} testid="emp-dept" />
        <FormField label="Sopimuksen tyyppi" value={form.employment.contract_type} onChange={(v) => setForm({ ...form, employment: { ...form.employment, contract_type: v } })} placeholder="toistaiseksi / määräaikainen" testid="emp-contract" />
        <FormField label="Alkupäivä" type="date" value={form.employment.start_date} onChange={(v) => setForm({ ...form, employment: { ...form.employment, start_date: v } })} testid="emp-start" />
        <FormField label="Päättymispäivä (jos määräaikainen)" type="date" value={form.employment.end_date} onChange={(v) => setForm({ ...form, employment: { ...form.employment, end_date: v } })} testid="emp-end" />
        <FormField label="Koeaika päättyy" type="date" value={form.employment.probation_end} onChange={(v) => setForm({ ...form, employment: { ...form.employment, probation_end: v } })} testid="emp-prob" />
        <FormField label="Lomapäiviä" type="number" value={form.employment.holiday_balance_days} onChange={(v) => setForm({ ...form, employment: { ...form.employment, holiday_balance_days: v ? parseFloat(v) : null } })} testid="emp-hol" />
        <FormField label="Tuntipalkka €/h" type="number" value={form.employment.hourly_rate} onChange={(v) => setForm({ ...form, employment: { ...form.employment, hourly_rate: v ? parseFloat(v) : null } })} testid="emp-rate" />
        <FormField label="Kuukausipalkka €" type="number" value={form.employment.monthly_salary} onChange={(v) => setForm({ ...form, employment: { ...form.employment, monthly_salary: v ? parseFloat(v) : null } })} testid="emp-month" />
        <FormField label="Viikkotunnit" type="number" value={form.employment.weekly_hours} onChange={(v) => setForm({ ...form, employment: { ...form.employment, weekly_hours: v ? parseFloat(v) : null } })} testid="emp-week" />
      </div>
      <Button onClick={() => save(form)} disabled={saving} className="btn-luxe" data-testid="employment-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3.5 h-3.5 mr-2" />}
        Tallenna työsuhteen tiedot
      </Button>
    </div>
  );
}

/* ── Certifications ──────────────────────────────────────────────────── */
function CertsTab({ data, save, saving }) {
  const [certs, setCerts] = useState(data.certifications || []);
  const addCert = () => setCerts([...certs, { id: `cert-${Date.now()}`, type: "", name: "", issuer: "", issued_date: "", expiry_date: "" }]);
  const updateCert = (i, k, v) => setCerts(certs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const removeCert = (i) => setCerts(certs.filter((_, idx) => idx !== i));
  return (
    <div className="glass-card p-5 space-y-3" data-testid="certs-form">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-widest text-amber-300/80">Pätevyydet & passit</p>
        <Button size="sm" variant="outline" onClick={addCert} className="border-emerald-400/30 text-emerald-200" data-testid="cert-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Lisää
        </Button>
      </div>
      {certs.length === 0 ? (
        <p className="text-sm text-white/30 italic text-center py-6">Ei pätevyyksiä</p>
      ) : (
        certs.map((c, i) => {
          const expired = c.expiry_date && new Date(c.expiry_date) < new Date();
          const expSoon = c.expiry_date && !expired && new Date(c.expiry_date) - new Date() < 90 * 86400000;
          return (
            <div key={c.id || i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid={`cert-row-${i}`}>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tyyppi</Label>
                  <select value={c.type || ""} onChange={(e) => updateCert(i, "type", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm mt-1" data-testid={`cert-type-${i}`}>
                    <option value="">Valitse…</option>
                    {CERT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <FormField label="Tarkka nimi" value={c.name} onChange={(v) => updateCert(i, "name", v)} testid={`cert-name-${i}`} />
                <FormField label="Myöntäjä" value={c.issuer} onChange={(v) => updateCert(i, "issuer", v)} testid={`cert-issuer-${i}`} />
                <FormField label="Myöntöpäivä" type="date" value={c.issued_date} onChange={(v) => updateCert(i, "issued_date", v)} testid={`cert-issued-${i}`} />
                <FormField label="Voimassa asti" type="date" value={c.expiry_date} onChange={(v) => updateCert(i, "expiry_date", v)} testid={`cert-expiry-${i}`} />
              </div>
              <div className="flex justify-between items-center pt-1">
                <div>
                  {expired && <Badge className="bg-red-500/15 text-red-300"><AlertCircle className="w-3 h-3 mr-1" /> Vanhentunut</Badge>}
                  {expSoon && <Badge className="bg-amber-500/15 text-amber-300"><AlertCircle className="w-3 h-3 mr-1" /> Vanhenee pian</Badge>}
                  {!expired && !expSoon && c.expiry_date && <Badge className="bg-emerald-500/15 text-emerald-300"><CheckCircle2 className="w-3 h-3 mr-1" /> Voimassa</Badge>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeCert(i)} className="text-red-300" data-testid={`cert-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          );
        })
      )}
      <Button onClick={() => save({ certifications: certs })} disabled={saving} className="btn-luxe" data-testid="certs-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3.5 h-3.5 mr-2" />}
        Tallenna pätevyydet
      </Button>
    </div>
  );
}

/* ── Documents ───────────────────────────────────────────────────────── */
function DocsTab({ data, staffId, reload }) {
  const [docs, setDocs] = useState(data.documents || []);
  const [uploading, setUploading] = useState(false);
  const [type, setType] = useState("other");
  const [description, setDescription] = useState("");

  useEffect(() => { setDocs(data.documents || []); }, [data.documents]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Tiedosto liian suuri (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      await api.post(`/admin/staff/${staffId}/documents`, {
        name: f.name,
        type,
        mime_type: f.type || "application/octet-stream",
        data_base64: b64,
        description: description || null,
      });
      toast.success("Liite lisätty");
      setDescription("");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setUploading(false);
    // Reset file input
    e.target.value = "";
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/admin/staff/${staffId}/documents/${doc.id}`);
      const blob = new Blob([Uint8Array.from(atob(r.data.data_base64), c => c.charCodeAt(0))], { type: r.data.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Poista "${doc.name}"?`)) return;
    try {
      await api.delete(`/admin/staff/${staffId}/documents/${doc.id}`);
      toast.success("Liite poistettu");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="docs-form">
      <div className="glass-card p-5 space-y-3">
        <p className="text-xs uppercase tracking-widest text-amber-300/80">Lisää liite</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Tyyppi</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm mt-1" data-testid="doc-type">
              <option value="tax_card">Verokortti</option>
              <option value="contract">Työsopimus</option>
              <option value="certificate">Todistus / passi</option>
              <option value="id">Henkilöllisyystodistus</option>
              <option value="other">Muu</option>
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Kuvaus (vapaaehtoinen)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="doc-desc" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Tiedosto (PDF / JPG / PNG, max 10MB)</Label>
        </div>
        <Input type="file" accept=".pdf,image/jpeg,image/png" onChange={onUpload} disabled={uploading} data-testid="doc-file" />
        {uploading && <p className="text-xs text-cyan-300"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Ladataan…</p>}
      </div>

      <div className="glass-card p-5">
        <p className="text-xs uppercase tracking-widest text-amber-300/80 mb-3">Liitteet ({docs.length})</p>
        {docs.length === 0 ? (
          <p className="text-sm text-white/30 italic text-center py-6">Ei liitteitä</p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`doc-row-${d.id}`}>
                <FileText className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{d.name}</p>
                  <p className="text-[10px] text-white/40">
                    {d.type} · {d.mime_type} · {(d.size_bytes / 1024).toFixed(1)} KB
                    {d.description && <> · {d.description}</>}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => download(d)} className="border-cyan-400/30" data-testid={`doc-dl-${d.id}`}>
                  <Upload className="w-3.5 h-3.5 rotate-180" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(d)} className="text-red-300" data-testid={`doc-del-${d.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Permissions ─────────────────────────────────────────────────────── */
function PermsTab({ data, save, saving }) {
  const [perms, setPerms] = useState(data.permissions || {});
  const update = (mk, key, val) => {
    setPerms((p) => ({
      ...p,
      [mk]: { ...(p[mk] || { access_level: "none" }), [key]: val },
    }));
  };
  const toggleAction = (mk, act) => {
    const cur = perms[mk]?.actions || [];
    update(mk, "actions", cur.includes(act) ? cur.filter(a => a !== act) : [...cur, act]);
  };
  return (
    <div className="glass-card p-5 space-y-3" data-testid="perms-form">
      <p className="text-xs uppercase tracking-widest text-amber-300/80">Moduulikohtaiset oikeudet</p>
      <p className="text-[11px] text-white/40">
        Jokaiselle moduulille: ei oikeutta, vain katselu, osittainen (valitse toiminnot) tai täydet.
        Lisäksi &quot;näkymä&quot; (vain omat / tiimin / kaikki) ja vapaa rooli moduulissa.
      </p>
      <div className="space-y-3">
        {MODULES.map((m) => {
          const p = perms[m.key] || { access_level: "none" };
          const lvl = ACCESS_LEVELS.find(l => l.value === p.access_level) || ACCESS_LEVELS[0];
          return (
            <div key={m.key} className={`rounded-xl border p-3 space-y-2 ${p.access_level === "none" ? "border-white/10 bg-white/[0.02]" : `border-white/15 ${lvl.bg}`}`} data-testid={`perm-${m.key}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium">{m.label}</span>
                  <span className={`text-[10px] uppercase ${lvl.color}`}>{lvl.label}</span>
                </div>
                <div className="flex gap-1 flex-wrap" data-testid={`perm-levels-${m.key}`}>
                  {ACCESS_LEVELS.map((lv) => (
                    <button
                      key={lv.value}
                      onClick={() => update(m.key, "access_level", lv.value)}
                      className={`px-2.5 py-1 rounded-full text-[10px] border transition ${
                        p.access_level === lv.value
                          ? `${lv.bg} border-white/20 ${lv.color} font-medium`
                          : "border-white/10 text-white/40 hover:text-white"
                      }`}
                      data-testid={`perm-level-${m.key}-${lv.value}`}
                    >
                      {lv.label}
                    </button>
                  ))}
                </div>
              </div>
              {p.access_level !== "none" && (
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
                  <div>
                    <Label className="text-[10px]">Näkymä (scope)</Label>
                    <select value={p.scope || "own"} onChange={(e) => update(m.key, "scope", e.target.value)} className="w-full bg-input/40 border border-white/10 rounded px-2 py-1 text-xs mt-0.5" data-testid={`perm-scope-${m.key}`}>
                      {SCOPES.map(sc => <option key={sc.value} value={sc.value}>{sc.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px]">Rooli moduulissa</Label>
                    <Input value={p.role || ""} onChange={(e) => update(m.key, "role", e.target.value)} placeholder="esim. Tarjoilija" className="mt-0.5 h-7 text-xs" data-testid={`perm-role-${m.key}`} />
                  </div>
                  {p.access_level === "partial" && (
                    <div className="col-span-2">
                      <Label className="text-[10px]">Sallitut toiminnot</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ACTIONS.map(a => (
                          <label key={a.value} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 cursor-pointer text-[10px]">
                            <input type="checkbox" checked={(p.actions || []).includes(a.value)} onChange={() => toggleAction(m.key, a.value)} data-testid={`perm-act-${m.key}-${a.value}`} />
                            {a.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Button onClick={() => save({ permissions: perms })} disabled={saving} className="btn-luxe" data-testid="perms-save">
        {saving ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <Save className="w-3.5 h-3.5 mr-2" />}
        Tallenna oikeudet
      </Button>
    </div>
  );
}

/* ── Reusable form field ─────────────────────────────────────────────── */
function FormField({ label, value, onChange, type = "text", placeholder, className = "", testid }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}
